import { Fragment, useRef, type CSSProperties, type KeyboardEvent, type RefCallback } from 'react';
import { WikilinkSuggestionPopup } from '@/components/WikilinkSuggestionPopup';
import { useWikilinkAutocomplete } from '@/hooks/useWikilinkAutocomplete';
import { classifyLines, type ContentFormatForLiveEditor } from '@/lib/liveEditorTokens';
import { cn } from '@/lib/utils';

interface LiveEditorProps {
  value: string;
  onChange: (value: string) => void;
  format: ContentFormatForLiveEditor;
  placeholder?: string;
  className?: string;
  /** Registers this surface's `<textarea>` as the toolbar's edit target — see `useDjotEditor`. */
  registerTextarea: RefCallback<HTMLTextAreaElement>;
  /** Format-specific keyboard shortcuts (bold/italic/link/…) shared with the raw-text surface. */
  onFormatKeyDown: (e: KeyboardEvent<HTMLTextAreaElement>) => void;
}

/**
 * The single source of truth for both layers' text metrics.
 *
 * Caret alignment depends entirely on the backdrop and the textarea
 * laying text out identically, so every property that affects glyph
 * position or line wrapping is declared **once**, here, and spread onto
 * both elements — rather than being written twice as Tailwind classes
 * that can silently drift apart.
 *
 * Explicit values (not Tailwind classes) are used deliberately:
 * `lineHeight` must be an exact number both layers share, and the
 * `<textarea>`'s UA stylesheet supplies its own `font`, `padding`, and
 * `line-height` defaults that have to be overridden unambiguously.
 */
const SHARED_METRICS: CSSProperties = {
  fontFamily:
    'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
  fontSize: '14px',
  lineHeight: '22px',
  letterSpacing: 'normal',
  tabSize: 2,
  padding: '16px',
  border: 0,
  // Both layers must break identically, including inside long unbroken
  // strings (URLs), or one will wrap a line the other doesn't.
  whiteSpace: 'pre-wrap',
  overflowWrap: 'break-word',
  wordBreak: 'normal',
};

/**
 * An editable "live edit" surface: unlike a read-only rendered preview,
 * this is a real `<textarea>` the user types directly into — styled to
 * hint at the rendered result (bold text, emphasized headings, tinted
 * code, coloured links) as they type, via the classic
 * "highlight-within-textarea" technique:
 *
 *  - The `<textarea>` has fully transparent text but a visible caret
 *    (`caretColor`) — it receives all real input/selection/focus, so
 *    every existing textarea-based feature (toolbar formatting,
 *    `[[wikilink]]` autocomplete, keyboard shortcuts, IME composition,
 *    mobile soft keyboards, native undo/redo, copy/paste) keeps working
 *    completely unchanged, with zero special-casing.
 *  - A non-interactive backdrop `<div>` sits behind it with *identical*
 *    metrics (see `SHARED_METRICS`), rendering the same text tokenized
 *    and styled per `@/lib/liveEditorTokens`.
 *
 * **The alignment contract.** The caret belongs to the textarea; the
 * visible text belongs to the backdrop. They only appear to be one
 * surface while both lay text out identically, so no style applied to
 * the backdrop's content may change text metrics — no font-size, no
 * line-height, no padding/margin/border on a line, no vertical-align,
 * no text-align, no white-space override. `liveEditorTokens` enforces
 * this on the styling side (its module doc explains the constraint in
 * full); this component enforces it on the layout side by spreading one
 * shared metric object onto both layers.
 *
 * This is why the live surface only *hints* at formatting — headings are
 * bolded rather than enlarged, lists aren't indented — instead of fully
 * mimicking the published article. A caret that lands exactly where the
 * user is typing matters far more in an editor than preview fidelity;
 * the split editor's read-only pane is the authoritative "how will this
 * look published" view.
 *
 * This intentionally does NOT use a `contentEditable` element — that
 * would require reimplementing IME/undo/paste/mobile keyboard behavior
 * from scratch and fighting React's DOM reconciliation for a
 * programmatically-rebuilt subtree.
 *
 * Note: the styling is an approximate, line-based classifier — not the
 * parser used for the published view — so it can be visually wrong on
 * ambiguous/nested markup. The `content` string itself is completely
 * unaffected either way.
 */
export function LiveEditor({
  value,
  onChange,
  format,
  placeholder,
  className,
  registerTextarea,
  onFormatKeyDown,
}: LiveEditorProps) {
  const localRef = useRef<HTMLTextAreaElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);

  const wikilink = useWikilinkAutocomplete({ textareaRef: localRef, value, onChange });

  const lines = classifyLines(value, format);

  const syncScroll = () => {
    const ta = localRef.current;
    const backdrop = backdropRef.current;
    if (ta && backdrop) {
      backdrop.scrollTop = ta.scrollTop;
      backdrop.scrollLeft = ta.scrollLeft;
    }
  };

  return (
    <div className={cn('relative h-full overflow-hidden', className)}>
      <div
        ref={backdropRef}
        aria-hidden
        className="pointer-events-none absolute inset-0 overflow-hidden"
        style={SHARED_METRICS}
      >
        {value === '' ? (
          <span className="text-muted-foreground">{placeholder}</span>
        ) : (
          lines.map((line, i) => (
            // An empty line still has to occupy exactly one line box, the
            // way the textarea counts a bare "\n". A <br/> guarantees
            // that; an empty text node or empty <span> would collapse to
            // zero height and shift every following line up. Note some
            // block branches (code fences, tables) emit a single token
            // holding the raw line, which is `''` for a blank line —
            // hence checking the joined text rather than token count.
            <div key={i} className={line.lineClassName || undefined}>
              {line.tokens.every((t) => t.text === '') ? (
                <br />
              ) : (
                line.tokens.map((token, j) =>
                  token.className ? (
                    <span key={j} className={token.className}>
                      {token.text}
                    </span>
                  ) : (
                    <Fragment key={j}>{token.text}</Fragment>
                  ),
                )
              )}
            </div>
          ))
        )}
      </div>

      {/* A bare <textarea>, not the shared <Textarea> UI component: that
          one ships its own padding, border, radius, and text size, which
          would silently fight SHARED_METRICS and reintroduce caret
          drift. Here every metric is set explicitly and identically to
          the backdrop's. */}
      <textarea
        ref={(el) => {
          localRef.current = el;
          registerTextarea(el);
        }}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={(e) => registerTextarea(e.currentTarget)}
        onKeyDown={(e) => {
          if (wikilink.isOpen && wikilink.handleKeyDown(e)) return;
          onFormatKeyDown(e);
        }}
        onSelect={wikilink.onSelectionChange}
        onClick={wikilink.onSelectionChange}
        onKeyUp={wikilink.onSelectionChange}
        onScroll={() => {
          syncScroll();
          wikilink.onSelectionChange();
        }}
        onBlur={wikilink.close}
        placeholder={value === '' ? undefined : placeholder}
        spellCheck
        className="relative block h-full w-full resize-none bg-transparent text-transparent outline-none selection:bg-primary/20"
        style={{
          ...SHARED_METRICS,
          caretColor: 'var(--foreground)',
          // The textarea is the only scrollable layer; the backdrop is
          // scrolled to match it in `syncScroll`.
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      />

      {wikilink.isOpen && wikilink.position && (
        <WikilinkSuggestionPopup
          position={wikilink.position}
          suggestions={wikilink.suggestions}
          activeIndex={wikilink.activeIndex}
          isLoading={wikilink.isLoading}
          query={wikilink.query}
          onHover={wikilink.setActiveIndex}
          onSelect={wikilink.select}
        />
      )}
    </div>
  );
}
